import { useState, useEffect, useCallback } from 'react';
import { readToken } from './AccountPanel';
import Switch from './Switch';
import { API_BASE } from '../utils/apiBase';

/**
 * Availability, as a control rather than a label.
 *
 * This used to read "Status: Active" no matter what — a decoration that told
 * nobody anything. It is now the switch your connections actually see.
 *
 * WHY IT CAN DISAGREE WITH YOU
 * Choosing "Active" is only half of being available; the other half is having
 * been seen. A flag set on Tuesday against a tab closed on Wednesday would keep
 * telling your team you are at your desk. The server therefore reports both what
 * you chose and whether it still holds, and this says so plainly instead of
 * showing a green light that has gone stale.
 *
 * Only connections can see it, in keeping with contact details. Broadcasting who
 * is at their desk to the whole directory would be a different product.
 */

// Comfortably inside the server's staleness window, so simply having the app
// open keeps "active" true without a burst of traffic.
const HEARTBEAT_MS = 4 * 60 * 1000;

function Footer() {
  const [signedIn, setSignedIn] = useState(() => !!readToken());
  const [presence, setPresence] = useState(null);
  const [peers, setPeers] = useState({ connections: 0, active: 0 });
  const [busy, setBusy] = useState(false);
  // "cannot reach the server" is NOT the same as "away", and showing the second
  // when the first is true is a lie the user cannot see through — they would
  // believe their team had been told they are unavailable. Tracked separately.
  const [reachable, setReachable] = useState(true);

  const call = useCallback(async (method, body) => {
    const tok = readToken();
    if (!tok) return null;
    try {
      const res = await fetch(`${API_BASE}/api/network/presence`, {
        method,
        headers: {
          Authorization: `Bearer ${tok}`,
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (res.status === 401) { setSignedIn(false); setReachable(true); return null; }
      if (!res.ok) { setReachable(false); return null; }
      setReachable(true);
      return res.json();
    } catch { setReachable(false); return null; }
  }, []);

  const refresh = useCallback(async () => {
    const d = await call('GET');
    if (!d?.ok) return;
    setPresence(d.presence);
    setPeers({ connections: d.connections, active: d.activeConnections });
  }, [call]);

  useEffect(() => {
    const onAuth = (e) => { setSignedIn(!!e.detail?.signedIn); if (!e.detail?.signedIn) setPresence(null); };
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  useEffect(() => { if (signedIn) refresh(); }, [signedIn, refresh]);

  // The heartbeat is what makes "active" true rather than merely claimed. It
  // stops while you are set to away — there is nothing to keep alive.
  useEffect(() => {
    if (!signedIn || presence?.chosen !== 'active') return undefined;
    const t = setInterval(refresh, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [signedIn, presence?.chosen, refresh]);

  const setAvailability = async (wantActive) => {
    if (!reachable || !presence) { setBusy(true); await refresh(); setBusy(false); return; }
    setBusy(true);
    const d = await call('PATCH', { presence: wantActive ? 'active' : 'away' });
    if (d?.ok) { setPresence(d.presence); refresh(); }
    setBusy(false);
  };


  // Green means "your team can see you are available right now" — so it must be
  // false whenever that cannot be true, not merely whenever the last value we
  // happened to hold said active.
  const unknown = signedIn && !reachable;
  const on = signedIn && reachable && presence?.state === 'active';
  const label = !signedIn ? 'Signed out'
    : busy ? 'Saving…'
    : unknown ? 'Status unknown'
    : !presence ? 'Checking…'
    : on ? 'Active'
    : presence.chosen === 'active' ? 'Away — idle'
    : 'Away';

  const title = !signedIn
    ? 'Sign in to show your team when you are available'
    : unknown
      ? 'Cannot reach the server, so your availability is not being reported. Nothing has been sent to your team. Click to retry.'
    : !presence
      ? 'Checking your availability…'
    : on
      ? `Your ${peers.connections} connection${peers.connections === 1 ? '' : 's'} can see you are available. Click to go away.`
      : presence?.stale
        ? 'You chose Active, but this account has not been seen recently, so your team sees you as away.'
        : 'Your connections see you as away. Click to become available.';

  return (
    <footer className="app-footer">
      <div className="brand">
        <div className="">© 2021-2025 VAUOM - Design to Construction Services.</div>
      </div>
      <div className="header-actions">
        {signedIn && peers.connections > 0 && (
          <span className="small muted" style={{ fontSize: 12, color: 'var(--muted)' }}>
            {peers.active} of {peers.connections} online
          </span>
        )}
        <Switch
          tone="status"
          on={on}
          busy={busy}
          disabled={!signedIn || unknown || !presence}
          onChange={setAvailability}
          label={label}
          title={title}
        />
      </div>
    </footer>
  );
}

export default Footer;
